/**
 * 7일 PRO 무료 체험 CTA 버튼 (재사용 컴포넌트)
 *
 * 동작:
 *  1. 마운트 시 — 로그인 상태면 GET /api/payment/subscription/trial-status 로 사용 여부 확인.
 *     - 이미 사용한 사용자에게는 `onAlreadyUsed` fallback 노출 (없으면 null 반환).
 *  2. 클릭 시 — 로그인 분기:
 *     - 로그인 + 미사용 → POST /api/payment/subscription/free-trial 호출 → 토스트 + reload
 *     - 비로그인 → setPendingFreeTrial() 호출 + /auth 페이지로 이동.
 *       회원가입 완료 후 FreeTrialAutoStarter가 자동으로 체험을 시작한다.
 *
 * variant:
 *  - 'home-final': 홈페이지 마무리 CTA 섹션 (밝은 배경 → 짙은 Primary 버튼)
 *  - 'adpro-final': 광고 랜딩의 어두운 Final CTA (흰색 outline → 호버 시 invert)
 *
 * 기존 ProUpgradeModal과 동일한 백엔드 API를 사용하므로 백엔드 변경 없음.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../Toast';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { trackFreeTrialClick, trackFreeTrialStart } from '../../utils/eventTracker';
import { setPendingFreeTrial } from '../../utils/freeTrialPending';
import {
  fetchTrialUsed, isTrialAlreadyUsedError, loadCachedTrialUsed, saveCachedTrialUsed,
} from '../../utils/trialStatus';
import {
  buildStartTrialMetaPayload,
  createStartTrialEventId,
  trackMetaStartTrialPixel,
} from '../../utils/paymentTracking';

const startFreeTrialApi = async (idToken, triggerFeature, triggerPage, eventId) => {
  const res = await fetch(`${getApiBaseUrl()}/api/payment/subscription/free-trial`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(buildStartTrialMetaPayload({
      eventId,
      triggerFeature,
      triggerPage,
    })),
  });
  return res.json();
};

const FreeTrialCTAButton = ({
  variant = 'home-final',
  triggerFeature = 'free_trial_cta',
  triggerPage,
  onAlreadyUsed = null,
  fullWidth = true,
  onVisibilityChange,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [trialUsed, setTrialUsed] = useState(null);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // 마지막으로 확인한 값을 먼저 보여 주고 서버 값으로 갱신한다(서버가 늦어도 버튼이 로딩에 묶이지 않음).
    const cached = loadCachedTrialUsed(currentUser?.uid);
    const checkTrialStatus = async () => {
      if (!currentUser) {
        if (!cancelled) setTrialUsed(false);
        return;
      }
      try {
        const { getAuth } = await import('firebase/auth');
        const user = getAuth().currentUser;
        if (!user) {
          if (!cancelled) setTrialUsed(false);
          return;
        }
        const idToken = await user.getIdToken();
        const trialUsedNow = await fetchTrialUsed(idToken, { uid: currentUser.uid });
        if (!cancelled) setTrialUsed(trialUsedNow);
      } catch (err) {
        console.warn('체험 상태 확인 실패 - 버튼 숨김 처리:', err);
        if (!cancelled && cached === null) setTrialUsed(true);
      }
    };

    setTrialUsed(cached);
    checkTrialStatus();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  useEffect(() => {
    if (typeof onVisibilityChange !== 'function') return;
    if (trialUsed === null) return;
    const visible = trialUsed === false || onAlreadyUsed !== null;
    onVisibilityChange(visible);
  }, [trialUsed, onAlreadyUsed, onVisibilityChange]);

  const resolvedTriggerPage = triggerPage || (typeof window !== 'undefined' ? window.location.pathname : '/');

  const handleClick = useCallback(async () => {
    if (isStarting) return;

    trackFreeTrialClick(triggerFeature);

    if (!currentUser) {
      setPendingFreeTrial({ triggerFeature, triggerPage: resolvedTriggerPage });
      navigate('/auth', { state: { intent: 'free_trial' } });
      return;
    }

    setIsStarting(true);
    try {
      const { getAuth } = await import('firebase/auth');
      const user = getAuth().currentUser;
      if (!user) {
        setPendingFreeTrial({ triggerFeature, triggerPage: resolvedTriggerPage });
        navigate('/auth', { state: { intent: 'free_trial' } });
        return;
      }
      const idToken = await user.getIdToken();
      const eventId = createStartTrialEventId();
      const result = await startFreeTrialApi(
        idToken,
        triggerFeature,
        resolvedTriggerPage,
        eventId,
      );

      if (result?.success) {
        trackFreeTrialStart(triggerFeature);
        saveCachedTrialUsed(currentUser.uid, true);
        trackMetaStartTrialPixel({
          contentName: triggerFeature,
          eventId: result.meta_event_id || eventId,
        });
        showToast('7일 PRO 무료 체험이 시작됐어요. 지금 바로 사용해보세요.', 'success', 5000);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        // FastAPI HTTPException은 {detail: "..."} 형태로 직렬화되므로 detail을 우선 표시.
        const reason = result?.detail || result?.error || '체험을 시작하지 못했어요. 잠시 후 다시 시도해주세요.';
        showToast(reason, 'error', 4000);
        if (isTrialAlreadyUsedError(reason)) saveCachedTrialUsed(currentUser.uid, true);
        setTrialUsed(true);
      }
    } catch (err) {
      console.error('무료 체험 시작 실패:', err);
      showToast('네트워크가 불안정해요. 잠시 후 다시 시도해주세요.', 'error', 4000);
    } finally {
      setIsStarting(false);
    }
  }, [currentUser, isStarting, navigate, resolvedTriggerPage, showToast, triggerFeature]);

  if (trialUsed === true) {
    return onAlreadyUsed === null || onAlreadyUsed === undefined ? null : onAlreadyUsed;
  }

  const isLoading = trialUsed === null || isStarting;
  const styles = getVariantStyles(variant, isLoading);

  return (
    <>
      <style>{cssRules}</style>
      <button
        type="button"
        className={`free-trial-cta-btn free-trial-cta-btn--${variant}`}
        style={{ ...styles.button, width: fullWidth ? '100%' : 'auto' }}
        onClick={handleClick}
        disabled={isStarting}
        aria-label="7일 PRO 무료 체험 시작"
      >
        {isStarting ? '시작 중...' : '7일 PRO 무료 체험'}
      </button>
    </>
  );
};

const getVariantStyles = (variant, isLoading) => {
  if (variant === 'adpro-final') {
    return {
      button: {
        background: 'transparent',
        color: '#ffffff',
        border: '2px solid rgba(255, 255, 255, 0.55)',
        borderRadius: '12px',
        padding: '12px 28px',
        cursor: isLoading ? 'wait' : 'pointer',
        opacity: isLoading ? 0.7 : 1,
        transition: 'all 0.2s ease',
        fontFamily: 'var(--whatquiz-font-family)',
        fontSize: '15px',
        fontWeight: 700,
        boxShadow: 'none',
      },
    };
  }
  return {
    button: {
      background: '#3182f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '12px',
      padding: '14px 32px',
      cursor: isLoading ? 'wait' : 'pointer',
      opacity: isLoading ? 0.7 : 1,
      transition: 'all 0.2s ease',
      fontFamily: 'var(--whatquiz-font-family)',
      fontSize: '16px',
      fontWeight: 700,
      boxShadow: '0 4px 12px rgba(49, 130, 246, 0.25)',
    },
  };
};

const cssRules = `
  .free-trial-cta-btn--home-final:hover:not(:disabled) {
    background: #1b64da !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(49, 130, 246, 0.35) !important;
  }
  .free-trial-cta-btn--home-final:active:not(:disabled) {
    transform: translateY(0);
  }
  .free-trial-cta-btn--adpro-final:hover:not(:disabled) {
    background: #ffffff !important;
    border-color: #ffffff !important;
    transform: translateY(-2px);
  }
  .free-trial-cta-btn--adpro-final:hover:not(:disabled) span {
    color: #3182f6 !important;
  }
  .free-trial-cta-btn:disabled {
    cursor: not-allowed;
  }
`;

export default FreeTrialCTAButton;
